'use client';
import ChevronDownIcon from '@heroicons/react/24/outline/ChevronDownIcon';
import CurrencyDollarIcon from '@heroicons/react/24/outline/CurrencyDollarIcon';
import TrashIcon from '@heroicons/react/24/outline/TrashIcon';
import { DebugPanel } from '@jci/components/DebugPanel';
import { Dropdown } from '@jci/components/Dropdown';
import { useSafeAction } from '@jci/components/safeAction/useSafeAction';
import type { PmDealer } from '@jci/core/siteServicesApi/jc2Schemas/jc2Dealer';
import cn from 'classnames';
import type { Session } from 'next-auth';
import { toast } from 'react-toastify';
import { isApprovedDealer } from '../../../../core/src/utils/userRoles';
import {
  getDealerBillingAction,
  leaveDealerAction,
  setActiveDealerAction,
} from '../../server/actions';

type Props = {
  activeDealer?: PmDealer | null;
  dealers?: PmDealer[];
  session?: Session | null;
};

export const Debug = ({ activeDealer, session, dealers }: Props) => {
  const setActiveDealer = useSafeAction(setActiveDealerAction, {
    onSuccess: ({ input: { dealerId } }) => {
      const dealerName = dealers?.find(
        (dealer) => dealer.dealer_id === dealerId,
      )?.dealer_name;
      toast.info(
        dealerId
          ? `Now viewing dealer "${dealerName}."`
          : 'No active dealer selected.',
      );
    },
  });
  const leaveDealer = useSafeAction(leaveDealerAction, {
    onSuccess: ({ input: { dealerId } }) => {
      const dealerName = dealers?.find(
        (dealer) => dealer.dealer_id === dealerId,
      )?.dealer_name;
      toast.info(`Left dealer "${dealerName}"`);
    },
  });
  const getBilling = useSafeAction(getDealerBillingAction, {
    onSuccess: ({ data }) => {
      if (!data) {
        toast.error('No Stripe customer found to redirect to.');
        return;
      }
      window.open(
        `https://dashboard.stripe.com/test/customers/${data.stripeCustomerId}`,
        '_blank',
      );
    },
  });

  return (
    <DebugPanel session={session}>
      <div className="flex gap-6 pt-2">
        <Dropdown
          className="dropdown-top"
          trigger={
            <div className="flex min-w-max cursor-pointer select-none items-center gap-3 rounded-full border py-2 pl-6 pr-5 shadow-sm hover:shadow">
              <div>
                <p className="text-md text-neutral-400">
                  {activeDealer?.dealer_id ?? ''}
                </p>
                <p className="text-lg">
                  {activeDealer?.dealer_name ?? '+ New dealer'}
                </p>
              </div>
              <ChevronDownIcon className="size-5" />
            </div>
          }
        >
          {dealers?.map((dealer) => (
            <li
              key={dealer.dealer_id}
              onClick={() =>
                setActiveDealer.execute({ dealerId: dealer.dealer_id })
              }
            >
              <div
                className={cn({
                  'flex min-w-40 flex-col items-start gap-0 px-5 py-4 text-sm': true,
                  active: dealer.dealer_id === activeDealer?.dealer_id,
                })}
              >
                <div className="text-md text-neutral-400">
                  {dealer.dealer_id}
                </div>
                <p className="text-lg">{dealer.dealer_name}</p>
                <p className="text-sm text-neutral-400">
                  ({isApprovedDealer(dealer) ? 'approved' : 'unapproved'} -{' '}
                  {dealer.pm_dealer_type ?? 'no dealer type'})
                </p>
              </div>
            </li>
          ))}
          <li onClick={() => setActiveDealer.execute({ dealerId: null })}>
            <div
              className={cn({
                'flex flex-col items-start gap-0 px-3 py-4 text-sm': true,
                active: !activeDealer,
              })}
            >
              <p className="text-lg">{'+ New dealer'}</p>
            </div>
          </li>
        </Dropdown>
        {activeDealer && (
          <div className="flex flex-col justify-center gap-2">
            <div className="tooltip" data-tip="Open Customer in Stripe">
              <CurrencyDollarIcon
                className={cn({
                  'btn btn-circle btn-xs': true,
                  loading: getBilling.isExecuting,
                })}
                onClick={() => getBilling.execute()}
              />
            </div>
            <div className="tooltip size-6" data-tip="Leave Dealer">
              <TrashIcon
                className={cn({
                  'btn btn-circle btn-xs': true,
                  loading: leaveDealer.isExecuting,
                })}
                onClick={() =>
                  leaveDealer.execute({ dealerId: activeDealer.dealer_id })
                }
              />
            </div>
          </div>
        )}
      </div>
    </DebugPanel>
  );
};
